import { api } from "../../api";
import { Order, ExchangeOrderStatus, OrderType, ServiceOrderStatus } from "../interface";

/**
 * Формат заявки, приходящий с zero-api (совместим с админкой).
 * Богатый — со всеми полями. Используется новой историей и деталями транзакции.
 */
export type HistoryOrderType =
  | "EXCHANGE"
  | "DEPOSIT"
  | "WITHDRAW"
  | "CARD_ISSUE"
  | "CARD_TOPUP"
  /**
   * Ручная корректировка баланса администратором. При положительной дельте
   * заполнены `buy_currency`/`buy_amount`, при отрицательной —
   * `sell_currency`/`sell_amount`. В `comment` — причина.
   */
  | "ADJUSTMENT"
  /**
   * Виртуальный тип: оплата по QR-коду СБП. Хранится в отдельной таблице
   * `SbpPayment`, id имеет формат `sbp-<N>`.
   */
  | "SBP_PAYOUT";

export type HistoryOrderStatus =
  | "NEW"
  | "PENDING"
  | "IN_PROGRESS"
  | "SUCCEEDED"
  | "CANCELLED"
  | "FAILED";

export interface HistoryOrder {
  id: string;
  type: HistoryOrderType;
  status: HistoryOrderStatus;
  sell_currency: string | null;
  buy_currency: string | null;
  sell_amount: string | null;
  buy_amount: string | null;
  rate: string | null;
  fee_amount: string | null;
  fee_currency: string | null;
  network_code: string | null;
  external_tx_hash: string | null;
  address: string | null;
  comment: string | null;
  admin_comment: string | null;
  meta: unknown;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface HistoryQuery {
  page?: number;
  pageSize?: number;
  types?: HistoryOrderType[];
  statuses?: HistoryOrderStatus[];
  dateFrom?: string;
  dateTo?: string;
  q?: string;
  /** Фильтр по карте — показывает только Orders, где `card_id` совпадает. */
  cardId?: string;
}

export interface HistoryEnvelope {
  items: HistoryOrder[];
  total: number;
  page: number;
  pageSize: number;
}

function toNumber(v: string | null | undefined): number {
  if (v === null || v === undefined || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function mapStatus(
  status: HistoryOrderStatus,
  type: HistoryOrderType
): ExchangeOrderStatus | ServiceOrderStatus {
  const isService = type === "CARD_ISSUE" || type === "CARD_TOPUP";
  if (isService) {
    if (status === "SUCCEEDED") return "COMPLETED";
    if (status === "CANCELLED" || status === "FAILED") return "CANCELLED";
    return "PENDING";
  }
  if (status === "SUCCEEDED") return "SUCCEEDED";
  if (status === "CANCELLED" || status === "FAILED") return "CANCELLED";
  if (status === "NEW") return "NEW";
  return "IN_PROGRESS";
}

function mapType(type: HistoryOrderType): OrderType {
  if (type === "CARD_ISSUE" || type === "CARD_TOPUP") return "SERVICE";
  // SBP_PAYOUT и ADJUSTMENT в legacy-модели трактуем как обмен —
  // формы предзаписей у них похожие.
  return "EXCHANGE";
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("ru-RU");
  } catch {
    return iso;
  }
}

function mapOrder(o: HistoryOrder): Order {
  const type = mapType(o.type);
  return {
    id: Number(o.id),
    order_type: type,
    status: mapStatus(o.status, o.type),
    sell_currency: o.sell_currency ?? o.buy_currency ?? "",
    buy_currency: o.buy_currency ?? o.sell_currency ?? "",
    sell_amount: toNumber(o.sell_amount),
    buy_amount: toNumber(o.buy_amount),
    exchange_rate: toNumber(o.rate),
    payment_method: o.network_code ?? "CARD",
    comment: o.comment ?? "",
    user_telegram_id: 0,
    promo_code_id: null,
    account_id: null,
    created_at: formatDate(o.created_at),
    buy_amount_without_discount: toNumber(o.buy_amount),
    discount_percentage: 0,
    service_name: type === "SERVICE" ? o.comment ?? undefined : undefined,
  };
}

/**
 * Legacy-обёртка для старых мест, которым нужен упрощённый
 * `Order[]` без пагинации.
 */
export async function getOrderHistory(): Promise<Order[]> {
  const res = await api.get<HistoryEnvelope>("/me/orders", {
    params: { page: 1, pageSize: 50 },
  });
  return res.data.items.map(mapOrder);
}

/**
 * Богатый листинг истории с фильтрами и пагинацией.
 * Используется новой страницей истории (`/history`).
 */
export async function getHistory(params: HistoryQuery = {}): Promise<HistoryEnvelope> {
  const query: Record<string, string | number> = {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  };
  if (params.types && params.types.length > 0) query.types = params.types.join(",");
  if (params.statuses && params.statuses.length > 0)
    query.statuses = params.statuses.join(",");
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.q) query.q = params.q;
  if (params.cardId) query.cardId = params.cardId;

  const res = await api.get<HistoryEnvelope>("/me/orders", { params: query });
  return res.data;
}

/**
 * Детали одной транзакции текущего пользователя.
 */
export async function getOrderById(id: string): Promise<HistoryOrder> {
  const res = await api.get<HistoryOrder>(`/me/orders/${id}`);
  return res.data;
}
